export type OfflineMissionRole = "employee" | "contractor";

export type OfflineMissionSyncState =
  | "available_offline"
  | "pending_changes"
  | "syncing"
  | "conflict"
  | "synchronized";

export type OfflineChecklistItem = {
  id: string;
  label: string;
  instructions: string | null;
  required: boolean;
  minimumPhotoCount: number;
  completed: boolean;
};

export type OfflineEvidence = {
  id: string;
  checklistItemId: string;
  localUri: string;
  mimeType: string;
  capturedAt: string;
  remoteStoragePath: string | null;
};

export type OfflineMissionActivity = {
  activityKey: string;
  activityLabel: string;
};

export type OfflineInventoryItem = {
  id: string;
  name: string;
  category: string | null;
  unit: string;
  itemKind: string;
  currentQuantity: number;
};

export type OfflineMissionBundle = {
  missionId: string;
  organizationId: string;
  profileId: string;
  role: OfflineMissionRole;
  serverVersion: string;
  workDate: string;
  startsAt: string;
  endsAt: string;
  location: string;
  clientDisplayName: string | null;
  contactName: string | null;
  contactPhone: string | null;
  instructions: string | null;
  checklistEditable?: boolean;
  checklist: OfflineChecklistItem[];
  evidence: OfflineEvidence[];
  reporting?: {
    mode: "individual" | "team_leader" | "team_member";
    assignedHours: number;
    members: Array<{
      workerId: string;
      displayName: string;
      assignedHours: number;
    }>;
  };
  activityCatalog?: OfflineMissionActivity[];
  inventoryCatalog?: OfflineInventoryItem[];
  downloadedAt: string;
  lastSynchronizedAt: string | null;
};

export type OfflineMissionMutationKind =
  | "checklist_item_set"
  | "evidence_added"
  | "evidence_removed"
  | "report_draft_saved"
  | "completion_requested";

export type OfflineMissionMutationStatus =
  | "pending"
  | "syncing"
  | "acknowledged"
  | "rejected"
  | "conflict";

export type OfflineMissionMutation = {
  id: string;
  idempotencyKey: string;
  missionId: string;
  organizationId: string;
  profileId: string;
  serverVersion: string;
  kind: OfflineMissionMutationKind;
  payload: Record<string, unknown>;
  createdAt: string;
  status: OfflineMissionMutationStatus;
  synchronizedAt: string | null;
  failureReason: string | null;
};

export type OfflineCompletionAssessment = {
  locallyComplete: boolean;
  canQueueCompletion: boolean;
  serverValidated: false;
  billable: false;
  missingRequiredItemIds: string[];
  missingPhotoItemIds: string[];
  pendingMutationCount: number;
  conflictCount: number;
};

export type OfflineApplyResult =
  | { status: "acknowledged"; synchronizedAt: string }
  | { status: "rejected"; reason: string }
  | { status: "conflict"; reason: string };

export interface OfflineMissionStore {
  saveMissionBundles(bundles: OfflineMissionBundle[]): Promise<void>;
  getMissionBundle(missionId: string): Promise<OfflineMissionBundle | null>;
  listMissionBundles(profileId: string, workDate: string): Promise<OfflineMissionBundle[]>;
  enqueueMutation(mutation: OfflineMissionMutation): Promise<boolean>;
  listMutations(missionId?: string): Promise<OfflineMissionMutation[]>;
  updateMutation(mutation: OfflineMissionMutation): Promise<void>;
  purgeProfile(profileId: string): Promise<void>;
}

export interface OfflineMissionGateway {
  downloadMissionDay(input: {
    profileId: string;
    role: OfflineMissionRole;
    workDate: string;
  }): Promise<OfflineMissionBundle[]>;
  applyMutation(mutation: OfflineMissionMutation): Promise<OfflineApplyResult>;
}

export function listBlockingOfflineMutations(
  mutations: OfflineMissionMutation[],
  profileId: string,
) {
  return mutations.filter(
    (mutation) =>
      mutation.profileId === profileId && mutation.status !== "acknowledged",
  );
}

function isImageEvidence(evidence: OfflineEvidence) {
  return evidence.mimeType.toLowerCase().startsWith("image/");
}

export function assessOfflineMissionCompletion(
  mission: OfflineMissionBundle,
  mutations: OfflineMissionMutation[],
): OfflineCompletionAssessment {
  const activeMutations = mutations.filter(
    (mutation) => mutation.missionId === mission.missionId,
  );
  const completedItems = new Map(
    mission.checklist.map((item) => [item.id, item.completed]),
  );
  const evidenceByItem = new Map<string, Set<string>>();

  mission.evidence.forEach((evidence) => {
    if (!isImageEvidence(evidence)) return;
    const entries = evidenceByItem.get(evidence.checklistItemId) ?? new Set<string>();
    entries.add(evidence.id);
    evidenceByItem.set(evidence.checklistItemId, entries);
  });

  activeMutations.forEach((mutation) => {
    if (["rejected", "conflict"].includes(mutation.status)) return;
    if (mutation.kind === "checklist_item_set") {
      const itemId = String(mutation.payload.checklistItemId ?? "");
      if (completedItems.has(itemId)) {
        completedItems.set(itemId, Boolean(mutation.payload.completed));
      }
    }
    if (mutation.kind === "evidence_added") {
      const itemId = String(mutation.payload.checklistItemId ?? "");
      const evidenceId = String(mutation.payload.evidenceId ?? mutation.id);
      const mimeType = String(mutation.payload.mimeType ?? "");
      if (itemId && mimeType.toLowerCase().startsWith("image/")) {
        const entries = evidenceByItem.get(itemId) ?? new Set<string>();
        entries.add(evidenceId);
        evidenceByItem.set(itemId, entries);
      }
    }
    if (mutation.kind === "evidence_removed") {
      const itemId = String(mutation.payload.checklistItemId ?? "");
      const evidenceId = String(mutation.payload.evidenceId ?? "");
      evidenceByItem.get(itemId)?.delete(evidenceId);
    }
  });

  const missingRequiredItemIds = mission.checklist
    .filter((item) => item.required && !completedItems.get(item.id))
    .map((item) => item.id);
  const missingPhotoItemIds = mission.checklist
    .filter(
      (item) =>
        (evidenceByItem.get(item.id)?.size ?? 0) <
        Math.max(0, item.minimumPhotoCount),
    )
    .map((item) => item.id);
  const conflictCount = activeMutations.filter(
    (mutation) => mutation.status === "conflict",
  ).length;
  const pendingMutationCount = activeMutations.filter((mutation) =>
    ["pending", "syncing"].includes(mutation.status),
  ).length;
  const locallyComplete =
    missingRequiredItemIds.length === 0 && missingPhotoItemIds.length === 0;

  return {
    locallyComplete,
    canQueueCompletion: locallyComplete && conflictCount === 0,
    serverValidated: false,
    billable: false,
    missingRequiredItemIds,
    missingPhotoItemIds,
    pendingMutationCount,
    conflictCount,
  };
}

export function createOfflineMissionMutation(input: {
  id: string;
  mission: OfflineMissionBundle;
  kind: OfflineMissionMutationKind;
  payload?: Record<string, unknown>;
  createdAt: string;
}): OfflineMissionMutation {
  if (!input.id.trim()) throw new Error("Un identifiant local est obligatoire.");
  return {
    id: input.id,
    idempotencyKey: `offline:${input.mission.organizationId}:${input.mission.missionId}:${input.id}`,
    missionId: input.mission.missionId,
    organizationId: input.mission.organizationId,
    profileId: input.mission.profileId,
    serverVersion: input.mission.serverVersion,
    kind: input.kind,
    payload: input.payload ?? {},
    createdAt: input.createdAt,
    status: "pending",
    synchronizedAt: null,
    failureReason: null,
  };
}

export class MemoryOfflineMissionStore implements OfflineMissionStore {
  private readonly missions = new Map<string, OfflineMissionBundle>();
  private readonly mutations = new Map<string, OfflineMissionMutation>();

  async saveMissionBundles(bundles: OfflineMissionBundle[]) {
    bundles.forEach((bundle) => this.missions.set(bundle.missionId, structuredClone(bundle)));
  }

  async getMissionBundle(missionId: string) {
    const mission = this.missions.get(missionId);
    return mission ? structuredClone(mission) : null;
  }

  async listMissionBundles(profileId: string, workDate: string) {
    return [...this.missions.values()]
      .filter((mission) => mission.profileId === profileId && mission.workDate === workDate)
      .sort((left, right) => left.startsAt.localeCompare(right.startsAt))
      .map((mission) => structuredClone(mission));
  }

  async enqueueMutation(mutation: OfflineMissionMutation) {
    if (this.mutations.has(mutation.idempotencyKey)) return false;
    this.mutations.set(mutation.idempotencyKey, structuredClone(mutation));
    return true;
  }

  async listMutations(missionId?: string) {
    return [...this.mutations.values()]
      .filter((mutation) => !missionId || mutation.missionId === missionId)
      .sort((left, right) => left.createdAt.localeCompare(right.createdAt))
      .map((mutation) => structuredClone(mutation));
  }

  async updateMutation(mutation: OfflineMissionMutation) {
    this.mutations.set(mutation.idempotencyKey, structuredClone(mutation));
  }

  async purgeProfile(profileId: string) {
    [...this.missions.entries()].forEach(([key, mission]) => {
      if (mission.profileId === profileId) this.missions.delete(key);
    });
    [...this.mutations.entries()].forEach(([key, mutation]) => {
      if (mutation.profileId === profileId) this.mutations.delete(key);
    });
  }
}

export class OfflineMissionSyncEngine {
  private readonly store: OfflineMissionStore;
  private readonly gateway: OfflineMissionGateway;

  constructor(store: OfflineMissionStore, gateway: OfflineMissionGateway) {
    this.store = store;
    this.gateway = gateway;
  }

  async downloadDay(input: {
    profileId: string;
    role: OfflineMissionRole;
    workDate: string;
  }) {
    const bundles = await this.gateway.downloadMissionDay(input);
    const invalidBundle = bundles.find(
      (bundle) => bundle.profileId !== input.profileId || bundle.role !== input.role,
    );
    if (invalidBundle) {
      throw new Error("Le serveur a retourné une mission hors du profil autorisé.");
    }
    await this.store.saveMissionBundles(bundles);
    return bundles;
  }

  async synchronizePending() {
    const mutations = (await this.store.listMutations()).filter(
      (mutation) => mutation.status === "pending",
    );
    const results: OfflineMissionMutation[] = [];
    for (const mutation of mutations) {
      const syncing = { ...mutation, status: "syncing" as const };
      await this.store.updateMutation(syncing);
      let response: OfflineApplyResult;
      try {
        response = await this.gateway.applyMutation(syncing);
      } catch (error) {
        const pending = {
          ...syncing,
          status: "pending" as const,
          failureReason: error instanceof Error ? error.message : "Synchronisation interrompue.",
        };
        await this.store.updateMutation(pending);
        results.push(pending);
        break;
      }
      const updated: OfflineMissionMutation = {
        ...syncing,
        status: response.status,
        synchronizedAt: response.status === "acknowledged" ? response.synchronizedAt : null,
        failureReason: response.status === "acknowledged" ? null : response.reason,
      };
      await this.store.updateMutation(updated);
      results.push(updated);
      if (response.status === "conflict") break;
    }
    return results;
  }
}
